import { ChainID } from '@jdiyield/constants'
import { Contract, PriceList, Stake, Token } from '@jdiyield/interfaces'
import throat from 'throat'
import Web3 from 'web3'

import copycatABI from '../abi/copycat.json'
import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { findToken, makeBatchRequest } from '../utils'

export const getCopycat = async (
  walletAddress: string,
  ct: Contract,
  tokens: Token[],
  priceList: PriceList,
  web3Prop: Web3
): Promise<Stake[]> => {
  try {
    const { contractAddress, rewardAddress, routerAddress, pendingReward } = ct
    const web3 = web3Prop || new Web3(getNodeUrl(56))
    const contract = new web3.eth.Contract(copycatABI as any, contractAddress)
    const _poolLength = await contract.methods
      .poolLength()
      .call()
      .catch(() => 1)
    const poolArray = [...Array(parseInt(_poolLength))]

    let _userInfoResults = []

    _userInfoResults = await makeBatchRequest(
      web3,
      poolArray.map((_data, poolId) => {
        return contract.methods.userInfo(poolId, walletAddress).call
      })
    )
    const _balances = _userInfoResults.map((result, poolId) => {
      let balance = '0'
      let rewardDebt = '0'
      balance = result?.amount || '0'
      rewardDebt = result.rewardDebt || '0'
      return { pool: poolId, balance: balance || '0', rewardDebt: rewardDebt || '0' }
    })

    const poolWhiteList = _balances.filter(({ balance }) => +balance > 0).map(({ pool }) => pool)
    const filteredBalance = _balances.filter(({ pool }) => poolWhiteList.includes(pool))
    const _balancesLP = await makeBatchRequest(
      web3,

      filteredBalance.map((b) => contract.methods.poolInfo(b.pool).call)
    )
    const _balancesLPResult = _balancesLP.map((obj, i) => ({ ...filteredBalance[i], lpAddress: obj['lpToken'] }))

    const _balanceLPPair = _balancesLPResult.map(
      throat(80, async (lp: any) => {
        return calculate(
          web3,
          {
            ...lp,
            reward: lp.rewardDebt,
            rewardAddress
          },
          routerAddress,
          tokens,
          priceList
        )
      })
    )

    const _pendingReward = async () => {
      try {
        const result = await makeBatchRequest(
          web3,
          _balancesLPResult
            .filter(({ pool }) => poolWhiteList.includes(pool))
            .map((lp) => {
              return contract.methods[`${pendingReward}`](lp.pool, walletAddress).call
            })
        )
        return result.map((reward, index) => ({ ..._balancesLPResult[index], reward }))
      } catch (error) {
        return _balancesLPResult.map((lp) => ({ ...lp, reward: 0 }))
      }
    }
    const [completeBalance, completePendingReward] = await Promise.all([Promise.all(_balanceLPPair), _pendingReward()])
    const rewardSymbol = findToken(tokens, rewardAddress)?.symbol ?? '???'
    const copycatPrice = priceList[rewardAddress]?.usd ?? 0
    const result = completeBalance
      .filter((obj) => obj !== null)
      .map((i) => {
        const reward = +completePendingReward?.find((a: any) => a.pool === i.pool)?.reward / 1e18 || 0
        return {
          ...i,
          ...ct,
          availableBalanceOf: '0',
          reward,
          rewardSymbol,
          rewardUSD: reward * copycatPrice
        } as Stake
      })
    return result
  } catch (e) {
    console.log('getCopycat', e)
    return []
  }
}

export async function getCopycatAPR(
  contractAddress: string,
  poolId: number,
  chainID: ChainID,
  getBonusMultiplier?: string
): Promise<{
  rewardPerBlock: string
  multiplier: string
  allocPoints: string
  totalAllocPoints: string
  stakeTokenAddress: string
}> {
  const RPC_URL = getNodeUrl(chainID)
  const web3 = new Web3(RPC_URL)
  const ct = new web3.eth.Contract(copycatABI as any, contractAddress)
  const poolInfo = await ct.methods.poolInfo(poolId).call()

  const rewardPerBlock = poolInfo.lastRewardBlock
  const stakeTokenAddress = poolInfo.lpToken
  const allocPoints = poolInfo.allocPoint
  const totalAllocPoints =
    (await ct.methods
      .totalAllocPoint()
      .call()
      .catch(() => allocPoints)) || +allocPoints

  let multiplier = '1'
  try {
    if (getBonusMultiplier && getBonusMultiplier !== 'none')
      multiplier = await ct.methods[`${getBonusMultiplier}`]().call()
  } catch (error) {
    multiplier = '1'
  }

  return {
    rewardPerBlock,
    multiplier,
    allocPoints,
    totalAllocPoints,
    stakeTokenAddress
  }
}
